import {
  Alert,
  Badge,
  Button,
  Group,
  Loader,
  NumberInput,
  Progress,
  SegmentedControl,
  Select,
  SimpleGrid,
  Stack,
  Switch,
  Table,
  Text,
  Tooltip,
} from '@mantine/core';
import { openConfirmModal } from '@mantine/modals';
import { showNotification } from '@mantine/notifications';
import {
  IconAlertTriangle,
  IconBolt,
  IconClock,
  IconCpu,
  IconDatabaseImport,
  IconPlayerPlay,
  IconShieldCheck,
} from '@tabler/icons-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useGPTStore } from '@/store';
import { getZendeskSources, importZendeskSource, type ZendeskSource } from '@mygpt/app';

import {
  backfillKnowledgeUnits,
  getKnowledgeLoopSettings,
  getKnowledgeLoopStatus,
  listKnowledgeLoopRuns,
  putKnowledgeLoopSettings,
  runPiiBackfill,
  triggerKnowledgeLoopRun,
  type KnowledgeLoopSettings,
  type KnowledgeLoopStatus,
  type KnowledgePiiBackfillReport,
  type KnowledgePublishMode,
  type KnowledgeRun,
  type KnowledgeSignal,
} from '@mygpt/app/apis/services/knowledgeLoop';
import { RunDetailModal } from './RunDetailModal';
import {
  CARD_BORDER,
  SIGNALS,
  errorDetail,
  errorStatus,
  formatDuration,
  formatJst,
  formatTokens,
  piiSummary,
  runStatusColor,
  sumInputs,
} from './shared';

const POLL_MS = 5000;
const SOURCE_POLL_MS = 10000;
const SOURCE_BUSY: ZendeskSource['status'][] = ['queued', 'importing', 'distilling'];
const HOURS = Array.from({ length: 24 }, (_, h) => ({ value: String(h), label: `${String(h).padStart(2, '0')}:00` }));

interface Props {
  datasetId: string;
  onOpenQueue: () => void;
  onOpenSources: () => void;
}

const Card = ({ title, right, children }: { title: string; right?: React.ReactNode; children: React.ReactNode }) => (
  <div style={{ border: CARD_BORDER, borderRadius: 12, background: '#fff' }}>
    <Group justify="space-between" px="md" py={10} style={{ borderBottom: CARD_BORDER }}>
      <Text fw={600} size="sm">
        {title}
      </Text>
      {right}
    </Group>
    <div style={{ padding: 16 }}>{children}</div>
  </div>
);

const Stat = ({ label, value, hint }: { label: string; value: React.ReactNode; hint?: string }) => (
  <Stack gap={2}>
    <Text size="xs" c="gray.6">
      {label}
    </Text>
    <Text fw={600} size="lg" style={{ fontVariantNumeric: 'tabular-nums', lineHeight: 1.2 }}>
      {value}
    </Text>
    {hint && (
      <Text size="xs" c="gray.5">
        {hint}
      </Text>
    )}
  </Stack>
);

/** 蒸留タブ：毎日蒸留の状態・設定・実行履歴を 1 か所で確認／変更する */
export const DistillPanel = ({ datasetId, onOpenQueue, onOpenSources }: Props) => {
  const { t } = useTranslation();
  const [status, setStatus] = useState<KnowledgeLoopStatus | null>(null);
  const [settings, setSettings] = useState<KnowledgeLoopSettings | null>(null);
  const [draft, setDraft] = useState<KnowledgeLoopSettings | null>(null);
  const [runs, setRuns] = useState<KnowledgeRun[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [starting, setStarting] = useState(false);
  const [backfilling, setBackfilling] = useState(false);
  const [runDetailId, setRunDetailId] = useState<string | null>(null);
  const [piiReport, setPiiReport] = useState<KnowledgePiiBackfillReport | null>(null);
  const [piiBusy, setPiiBusy] = useState<'scan' | 'apply' | null>(null);
  const wasRunning = useRef(false);
  // Zendesk 取込元。「取り込んで抽出」はここから全件を順に投入する（取込の後に抽出が続く）
  const [sources, setSources] = useState<ZendeskSource[]>([]);
  const [importing, setImporting] = useState(false);

  const loadStatus = useCallback(async () => {
    const [{ data: st }, { data: rs }] = await Promise.all([
      getKnowledgeLoopStatus(datasetId),
      listKnowledgeLoopRuns(datasetId, { limit: 15 }),
    ]);
    setStatus(st);
    setRuns(rs.items || []);
    return st;
  }, [datasetId]);
  const loadSources = useCallback(async () => {
    try {
      const { data } = await getZendeskSources(datasetId);
      setSources(data.sources || []);
    } catch (e) {
      console.error('load zendesk sources failed:', e);
    }
  }, [datasetId]);

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        const [{ data: cfg }] = await Promise.all([getKnowledgeLoopSettings(datasetId), loadStatus(), loadSources()]);
        if (!alive) return;
        setSettings(cfg);
        setDraft(cfg);
      } catch (e) {
        console.error('load knowledge loop failed:', e);
        showNotification({ color: 'red', message: t('knowledge_loop.load_error') });
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [datasetId, loadStatus, loadSources, t]);

  // 実行中だけポーリング。終わった瞬間にもう一度読み直して結果を出す
  const running = status?.running || null;
  useEffect(() => {
    if (!running) {
      if (wasRunning.current) {
        wasRunning.current = false;
        loadStatus().catch(() => undefined);
      }
      return undefined;
    }
    wasRunning.current = true;
    const timer = window.setInterval(() => loadStatus().catch(() => undefined), POLL_MS);
    return () => window.clearInterval(timer);
  }, [running, loadStatus]);

  // 取込中は取込元と抽出の状態を一緒にポーリング（取込が終わると抽出が始まる）
  const importBusy = sources.some((s) => SOURCE_BUSY.includes(s.status));
  useEffect(() => {
    if (!importBusy) return undefined;
    const timer = window.setInterval(() => {
      loadSources();
      loadStatus().catch(() => undefined);
    }, SOURCE_POLL_MS);
    return () => window.clearInterval(timer);
  }, [importBusy, loadSources, loadStatus]);
  const lastImportAt = useMemo(
    () =>
      sources
        .map((s) => s.last_run_at)
        .filter(Boolean)
        .sort()
        .slice(-1)[0] || null,
    [sources]
  );

  const dirty = useMemo(() => JSON.stringify(draft) !== JSON.stringify(settings), [draft, settings]);
  const update = (patch: Partial<KnowledgeLoopSettings>) => setDraft((d) => (d ? { ...d, ...patch } : d));

  // 抽出・統合・判定に使うモデル：平台の有効モデル + 「既定（環境の設定）」
  const { configInfo } = useGPTStore();
  const modelOptions = useMemo(() => {
    const models = (configInfo?.default_models || [])
      .filter((m) => m.enabled)
      .map((m) => ({ value: m.value, label: m.label }));
    const current = draft?.llm_model;
    if (current && !models.some((m) => m.value === current)) models.push({ value: current, label: current });
    return [
      { value: '', label: t('knowledge_loop.distill.llm_model_default', { model: draft?.llm_model_default || '' }) },
      ...models,
    ];
  }, [configInfo, draft?.llm_model, draft?.llm_model_default, t]);

  const save = async (patch?: Partial<KnowledgeLoopSettings>) => {
    if (!draft) return;
    setSaving(true);
    try {
      const body = patch || {
        run_hour_jst: draft.run_hour_jst,
        signals: draft.signals,
        publish_mode: draft.publish_mode,
        auto_min_confidence: draft.auto_min_confidence,
        auto_min_evidence: draft.auto_min_evidence,
        max_items_per_run: draft.max_items_per_run,
        daily_token_budget: draft.daily_token_budget,
        llm_model: draft.llm_model || '',
      };
      const { data } = await putKnowledgeLoopSettings(datasetId, body);
      setSettings(data);
      setDraft((d) => (patch && d ? { ...d, ...patch } : data));
      showNotification({ color: 'green', message: t('knowledge_loop.distill.saved') });
      loadStatus().catch(() => undefined);
    } catch (e) {
      showNotification({ color: 'red', message: errorDetail(e) || t('knowledge_loop.save_error') });
    } finally {
      setSaving(false);
    }
  };

  const runNow = async () => {
    setStarting(true);
    try {
      await triggerKnowledgeLoopRun(datasetId, false);
      showNotification({ color: 'green', message: t('knowledge_loop.distill.run_started') });
      await loadStatus();
    } catch (e: any) {
      const conflict = errorStatus(e) === 409;
      showNotification({
        color: conflict ? 'yellow' : 'red',
        message: conflict ? t('knowledge_loop.distill.already_running') : t('knowledge_loop.distill.run_error'),
      });
    } finally {
      setStarting(false);
    }
  };

  const importNow = async () => {
    setImporting(true);
    try {
      for (const source of sources) {
        if (!SOURCE_BUSY.includes(source.status)) await importZendeskSource(datasetId, source.id);
      }
      showNotification({ color: 'green', message: t('knowledge_loop.distill.import_started') });
      await loadSources();
    } catch (e: any) {
      const conflict = errorStatus(e) === 409;
      showNotification({
        color: conflict ? 'yellow' : 'red',
        message: conflict
          ? t('knowledge_loop.distill.import_running')
          : errorDetail(e) || t('knowledge_loop.distill.import_error'),
      });
    } finally {
      setImporting(false);
    }
  };

  const PII_SECTIONS = ['source_items', 'evidence', 'candidates', 'published', 'memories'] as const;
  const piiHasHits = !!piiReport && PII_SECTIONS.some((k) => piiReport[k].with_pii > 0);
  const runPii = async (apply: boolean) => {
    setPiiBusy(apply ? 'apply' : 'scan');
    try {
      const { data } = await runPiiBackfill(datasetId, apply);
      setPiiReport(data);
      if (apply) showNotification({ color: 'green', message: t('knowledge_loop.pii.backfill.done') });
    } catch (e) {
      showNotification({ color: 'red', message: errorDetail(e) || t('knowledge_loop.load_error') });
    } finally {
      setPiiBusy(null);
    }
  };
  const confirmPiiApply = () =>
    openConfirmModal({
      title: t('knowledge_loop.pii.backfill.apply_confirm_title'),
      centered: true,
      children: <Text size="sm">{t('knowledge_loop.pii.backfill.apply_confirm')}</Text>,
      labels: { confirm: t('knowledge_loop.pii.backfill.apply'), cancel: t('knowledge_loop.cancel') },
      confirmProps: { color: 'orange' },
      onConfirm: () => runPii(true),
    });

  const backfill = async () => {
    setBackfilling(true);
    try {
      const { data } = await backfillKnowledgeUnits(datasetId);
      showNotification({
        color: 'green',
        message: t('knowledge_loop.distill.backfill_done', { created: data.created, scanned: data.scanned }),
      });
      await loadStatus();
    } catch (e) {
      showNotification({ color: 'red', message: t('knowledge_loop.save_error') });
    } finally {
      setBackfilling(false);
    }
  };

  if (loading || !draft || !status) {
    return (
      <Group justify="center" py={60}>
        <Loader size="sm" />
      </Group>
    );
  }

  const last = status.last_run;
  const runStats = running?.stats || {};
  const extractPct = runStats.items_total ? ((runStats.items_done || 0) / runStats.items_total) * 100 : 0;
  const mergePct = runStats.clusters ? ((runStats.clusters_done || 0) / runStats.clusters) * 100 : 0;
  const progress = runStats.phase === 'consolidate' ? 60 + mergePct * 0.4 : extractPct * 0.6;
  const autoMode = draft.publish_mode !== 'manual';

  return (
    <Stack gap="md">
      {status.source_items === 0 && (
        <Alert color="blue" variant="light" icon={<IconDatabaseImport size={16} />}>
          <Group justify="space-between" wrap="nowrap">
            {sources.length > 0 ? (
              <Text size="sm">
                {t('knowledge_loop.distill.no_items_yet', { action: t('knowledge_loop.distill.import_now') })}
              </Text>
            ) : (
              <>
                <Text size="sm">{t('knowledge_loop.distill.no_source')}</Text>
                <Button size="xs" variant="light" onClick={onOpenSources}>
                  {t('knowledge_loop.distill.go_sources')}
                </Button>
              </>
            )}
          </Group>
        </Alert>
      )}

      <Card
        title={t('knowledge_loop.distill.status_title')}
        right={
          <Group gap="sm">
            <Switch
              checked={draft.enabled}
              disabled={saving}
              onChange={(e) => save({ enabled: e.currentTarget.checked })}
              label={draft.enabled ? t('knowledge_loop.distill.enabled_on') : t('knowledge_loop.distill.enabled_off')}
              size="sm"
            />
            {sources.length > 0 && (
              <Tooltip
                label={lastImportAt ? t('knowledge_loop.distill.last_import', { time: formatJst(lastImportAt) }) : ''}
                disabled={!lastImportAt}
                withArrow
              >
                <Button
                  size="xs"
                  variant="light"
                  leftSection={<IconDatabaseImport size={14} />}
                  loading={importing}
                  disabled={importBusy || !!running}
                  onClick={importNow}
                >
                  {importBusy ? t('knowledge_loop.distill.import_running') : t('knowledge_loop.distill.import_now')}
                </Button>
              </Tooltip>
            )}
            <Button
              size="xs"
              leftSection={<IconPlayerPlay size={14} />}
              loading={starting}
              disabled={!!running}
              onClick={runNow}
            >
              {t('knowledge_loop.distill.run_now')}
            </Button>
          </Group>
        }
      >
        {running ? (
          <Stack gap={8}>
            <Group gap={8}>
              <Loader size={14} />
              <Text size="sm" fw={500}>
                {t(`knowledge_loop.distill.phase_${runStats.phase || 'collect'}`)}
              </Text>
              <Text size="xs" c="gray.6">
                {t('knowledge_loop.distill.progress_line', {
                  done: runStats.items_done || 0,
                  total: runStats.items_total || 0,
                  candidates: runStats.candidates || 0,
                })}
              </Text>
            </Group>
            <Progress value={Math.min(100, progress)} size="sm" animated />
          </Stack>
        ) : (
          <SimpleGrid cols={{ base: 2, md: 5 }} spacing="lg">
            <Stat
              label={t('knowledge_loop.distill.next_run')}
              value={draft.enabled ? formatJst(status.next_run_at) : '—'}
              hint={draft.enabled ? 'JST' : t('knowledge_loop.distill.enabled_off')}
            />
            <Stat
              label={t('knowledge_loop.distill.last_run')}
              value={last ? formatJst(last.started_at) : '—'}
              hint={last ? t(`knowledge_loop.run_status.${last.status}`) : undefined}
            />
            <Stat
              label={t('knowledge_loop.distill.pending_items')}
              value={status.pending_items}
              hint={t('knowledge_loop.distill.pending_items_hint', { total: status.source_items })}
            />
            <Stack gap={2}>
              <Text size="xs" c="gray.6">
                {t('knowledge_loop.distill.queue')}
              </Text>
              <Group gap={6} align="baseline">
                <Text fw={600} size="lg" style={{ fontVariantNumeric: 'tabular-nums', lineHeight: 1.2 }}>
                  {status.queue.total}
                </Text>
                {status.queue.total > 0 && (
                  <Button size="compact-xs" variant="subtle" onClick={onOpenQueue}>
                    {t('knowledge_loop.distill.open_queue')}
                  </Button>
                )}
              </Group>
              <Text size="xs" c="gray.5">
                {t('knowledge_loop.distill.queue_hint', {
                  add: status.queue.add,
                  update: status.queue.update,
                  conflict: status.queue.conflict,
                })}
              </Text>
            </Stack>
            <Stat label={t('knowledge_loop.distill.approved_units')} value={status.approved_units} />
          </SimpleGrid>
        )}
        {!running && last && (
          <Text size="xs" c="gray.6" mt="md">
            {t('knowledge_loop.distill.last_summary', {
              inputs: sumInputs(last.stats.inputs),
              add: last.stats.add || 0,
              update: last.stats.update || 0,
              noop: last.stats.noop || 0,
              conflict: last.stats.conflict || 0,
              memories: last.stats.memories_used || 0,
              duration: formatDuration(last.stats.duration_sec),
              tokens: formatTokens(last.stats.tokens),
            })}
          </Text>
        )}
        {!running && last?.status === 'error' && last.error && (
          <Alert color="red" variant="light" mt="sm" icon={<IconAlertTriangle size={16} />}>
            <Text size="xs" style={{ wordBreak: 'break-all' }}>
              {last.error}
            </Text>
          </Alert>
        )}
      </Card>

      <Card
        title={t('knowledge_loop.distill.settings_title')}
        right={
          <Button size="xs" disabled={!dirty} loading={saving} onClick={() => save()}>
            {t('knowledge_loop.save')}
          </Button>
        }
      >
        <SimpleGrid cols={{ base: 1, md: 2 }} spacing="xl">
          <Stack gap="md">
            <Select
              label={t('knowledge_loop.distill.run_hour')}
              description={t('knowledge_loop.distill.run_hour_desc')}
              leftSection={<IconClock size={14} />}
              data={HOURS}
              value={String(draft.run_hour_jst)}
              onChange={(v) => v !== null && update({ run_hour_jst: Number(v) })}
              allowDeselect={false}
              w={200}
            />
            <Select
              label={t('knowledge_loop.distill.llm_model')}
              description={t('knowledge_loop.distill.llm_model_desc')}
              leftSection={<IconCpu size={14} />}
              data={modelOptions}
              value={draft.llm_model || ''}
              onChange={(v) => update({ llm_model: v || null })}
              allowDeselect={false}
              w={320}
            />
            <Stack gap={6}>
              <Text size="sm" fw={500}>
                {t('knowledge_loop.distill.signals')}
              </Text>
              <Text size="xs" c="gray.6">
                {t('knowledge_loop.distill.signals_desc')}
              </Text>
              {SIGNALS.map((s: KnowledgeSignal) => (
                <Switch
                  key={s}
                  size="sm"
                  checked={!!draft.signals[s]}
                  onChange={(e) => update({ signals: { ...draft.signals, [s]: e.currentTarget.checked } })}
                  label={t(`knowledge_loop.signal.${s}`)}
                  description={t(`knowledge_loop.signal_desc.${s}`)}
                />
              ))}
            </Stack>
            <Switch
              size="sm"
              checked={draft.pii_check_enabled !== false}
              onChange={(e) => update({ pii_check_enabled: e.currentTarget.checked })}
              label={t('knowledge_loop.distill.pii_check')}
              description={t('knowledge_loop.distill.pii_check_desc')}
            />
          </Stack>
          <Stack gap="md">
            <Stack gap={6}>
              <Text size="sm" fw={500}>
                {t('knowledge_loop.distill.publish_mode')}
              </Text>
              <SegmentedControl
                size="xs"
                value={draft.publish_mode}
                onChange={(v) => update({ publish_mode: v as KnowledgePublishMode })}
                data={(['manual', 'auto_add', 'auto_add_update'] as KnowledgePublishMode[]).map((m) => ({
                  value: m,
                  label: t(`knowledge_loop.publish_mode.${m}`),
                }))}
              />
              <Text size="xs" c="gray.6">
                {t(`knowledge_loop.publish_mode_desc.${draft.publish_mode}`)}
              </Text>
            </Stack>
            <Group grow align="flex-start">
              <NumberInput
                label={t('knowledge_loop.distill.auto_min_confidence')}
                disabled={!autoMode}
                min={0.5}
                max={1}
                step={0.05}
                decimalScale={2}
                value={draft.auto_min_confidence}
                onChange={(v) => typeof v === 'number' && update({ auto_min_confidence: v })}
              />
              <NumberInput
                label={t('knowledge_loop.distill.auto_min_evidence')}
                disabled={!autoMode}
                min={1}
                max={20}
                value={draft.auto_min_evidence}
                onChange={(v) => typeof v === 'number' && update({ auto_min_evidence: v })}
              />
            </Group>
            <Group grow align="flex-start">
              <NumberInput
                label={t('knowledge_loop.distill.max_items')}
                description={t('knowledge_loop.distill.max_items_desc')}
                min={10}
                max={5000}
                step={50}
                value={draft.max_items_per_run}
                onChange={(v) => typeof v === 'number' && update({ max_items_per_run: v })}
              />
              <NumberInput
                label={t('knowledge_loop.distill.token_budget')}
                description={t('knowledge_loop.distill.token_budget_desc')}
                min={50000}
                max={50000000}
                step={100000}
                thousandSeparator=","
                value={draft.daily_token_budget}
                onChange={(v) => typeof v === 'number' && update({ daily_token_budget: v })}
              />
            </Group>
          </Stack>
        </SimpleGrid>
      </Card>

      <Card
        title={t('knowledge_loop.pii.backfill.title')}
        right={
          <Group gap={8}>
            <Button
              size="xs"
              variant="light"
              leftSection={<IconShieldCheck size={14} />}
              loading={piiBusy === 'scan'}
              disabled={!!piiBusy}
              onClick={() => runPii(false)}
            >
              {t('knowledge_loop.pii.backfill.scan')}
            </Button>
            {piiHasHits && !piiReport?.apply && (
              <Button
                size="xs"
                color="orange"
                loading={piiBusy === 'apply'}
                disabled={!!piiBusy}
                onClick={confirmPiiApply}
              >
                {t('knowledge_loop.pii.backfill.apply')}
              </Button>
            )}
          </Group>
        }
      >
        <Text size="xs" c="gray.6" mb={piiReport ? 'sm' : 0}>
          {t('knowledge_loop.pii.backfill.desc')}
        </Text>
        {piiReport && (
          <Stack gap={4}>
            {PII_SECTIONS.map((k) => (
              <Text key={k} size="xs" c={piiReport[k].with_pii ? 'orange.8' : 'gray.7'}>
                {t('knowledge_loop.pii.backfill.row', {
                  label: t(`knowledge_loop.pii.backfill.section.${k}`),
                  hit: piiReport[k].with_pii,
                  total: piiReport[k].scanned,
                })}
                {piiReport[k].with_pii ? `（${piiSummary(piiReport[k].counts)}）` : ''}
              </Text>
            ))}
            {!piiHasHits && (
              <Text size="xs" c="teal.7">
                {t('knowledge_loop.pii.backfill.clean')}
              </Text>
            )}
            {piiReport.published.units.length > 0 && (
              <Stack gap={2} mt={4}>
                <Text size="xs" fw={600}>
                  {t('knowledge_loop.pii.backfill.published_list')}
                </Text>
                {piiReport.published.units.map((u) => (
                  <Text key={u.id} size="xs" c="red.7">
                    ・{u.title}
                  </Text>
                ))}
              </Stack>
            )}
          </Stack>
        )}
      </Card>

      <Card
        title={t('knowledge_loop.distill.history_title')}
        right={
          <Tooltip label={t('knowledge_loop.distill.backfill_desc')} multiline w={300} withArrow>
            <Button
              size="xs"
              variant="subtle"
              leftSection={<IconBolt size={14} />}
              loading={backfilling}
              onClick={backfill}
            >
              {t('knowledge_loop.distill.backfill')}
            </Button>
          </Tooltip>
        }
      >
        {runs.length === 0 ? (
          <Text size="sm" c="gray.5" ta="center" py="lg">
            {t('knowledge_loop.distill.no_runs')}
          </Text>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <Table verticalSpacing={6} highlightOnHover style={{ fontVariantNumeric: 'tabular-nums' }}>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>{t('knowledge_loop.distill.col_started')}</Table.Th>
                  <Table.Th>{t('knowledge_loop.distill.col_trigger')}</Table.Th>
                  <Table.Th>{t('knowledge_loop.distill.col_inputs')}</Table.Th>
                  <Table.Th>{t('knowledge_loop.distill.col_decisions')}</Table.Th>
                  <Table.Th>{t('knowledge_loop.distill.col_auto')}</Table.Th>
                  <Table.Th>{t('knowledge_loop.distill.col_memories')}</Table.Th>
                  <Table.Th>{t('knowledge_loop.distill.col_tokens')}</Table.Th>
                  <Table.Th>{t('knowledge_loop.distill.col_duration')}</Table.Th>
                  <Table.Th>{t('knowledge_loop.distill.col_status')}</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {runs.map((r) => (
                  <Table.Tr key={r.id} style={{ cursor: 'pointer' }} onClick={() => setRunDetailId(r.id)}>
                    <Table.Td>{formatJst(r.started_at)}</Table.Td>
                    <Table.Td>{t(`knowledge_loop.trigger.${r.trigger}`)}</Table.Td>
                    <Table.Td>
                      <Tooltip
                        withArrow
                        label={SIGNALS.map(
                          (s) => `${t(`knowledge_loop.signal.${s}`)} ${r.stats.inputs?.[s] || 0}`
                        ).join(' / ')}
                      >
                        <span>{sumInputs(r.stats.inputs)}</span>
                      </Tooltip>
                    </Table.Td>
                    <Table.Td>
                      <Text size="xs">
                        {t('knowledge_loop.distill.decisions_line', {
                          add: r.stats.add || 0,
                          update: r.stats.update || 0,
                          noop: r.stats.noop || 0,
                          conflict: r.stats.conflict || 0,
                        })}
                      </Text>
                    </Table.Td>
                    <Table.Td>{r.stats.auto_published || 0}</Table.Td>
                    <Table.Td>{r.stats.memories_used || 0}</Table.Td>
                    <Table.Td>{formatTokens(r.stats.tokens)}</Table.Td>
                    <Table.Td>{formatDuration(r.stats.duration_sec)}</Table.Td>
                    <Table.Td>
                      <Badge size="sm" variant="light" color={runStatusColor(r.status)}>
                        {t(`knowledge_loop.run_status.${r.status}`)}
                      </Badge>
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </div>
        )}
      </Card>

      <RunDetailModal
        datasetId={datasetId}
        runId={runDetailId}
        onClose={() => setRunDetailId(null)}
        onRedo={() => loadStatus().catch(() => undefined)}
      />
    </Stack>
  );
};
